import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoIcon from '@mui/icons-material/Info';
import { Box, BoxProps, MenuItem, MenuItemProps, SvgIconProps } from '@mui/material';
import { ChangeEvent, ReactElement, ReactNode } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { theme } from 'styles';
import { UserRole } from 'types';

import { BaseTextField } from '../BaseTextField';
import { BaseTextFieldControllerProps } from '../BaseTextFieldController';
import { FieldErrorBox, FieldErrorText } from '../BaseTextFieldController/styled';
import { CustomSelectedValue } from '../CustomSelectedValue';
import { Placeholder, MultipleSelectWrap } from './styled';
import { defaultStyles, disableStyles, menuItemStyles, multipleSelectStyles } from './styles';

function SelectExpandIcon(props: SvgIconProps) {
  return (
    <ExpandMoreRoundedIcon
      {...props}
      style={{
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '24px',
        pointerEvents: 'none',
      }}
    />
  );
}

type SelectControllerProps<T extends FieldValues> = BaseTextFieldControllerProps<T> & {
  options: {
    value: string;
    label: ReactNode;
    icon?: string;
    disabled?: boolean;
    role?: string;
    isManager?: boolean;
  }[];
  defaultOptionText?: string;
  multipleSelect?: boolean;
  CustomSelectLabel?: ReactElement;
  withDefaultEmptyOption?: boolean;
  emptyOption?: string;
  optionSx?: MenuItemProps['sx'];
  dropdownContainerSx?: BoxProps['sx'];
  handleChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disableDropdown?: boolean;
  categoryLength?: number;
  placeholder?: ReactNode;
  isIconSelect?: boolean;
};

export function SelectController<T extends FieldValues>({
  emptyOption,
  options,
  name,
  control,
  rules,
  defaultOptionText,
  sx,
  placeholder,
  multipleSelect = false,
  CustomSelectLabel,
  withDefaultEmptyOption,
  optionSx,
  dropdownContainerSx,
  handleChange,
  type,
  disableDropdown = false,
  categoryLength = 0,
  isIconSelect = false,
  FormHelperTextProps: callerFormHelperTextProps,
  ...otherProps
}: SelectControllerProps<T>): ReactElement {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const { t } = useTranslation();

  const emptyOptionText = emptyOption || t('common.noneOption');

  const iconSelectSx = isIconSelect
    ? {
        width: '56px',
        height: '56px',
        '& .MuiInputBase-root': {
          width: '56px',
          height: '56px',
          overflow: 'hidden',
          border: Boolean(error) ? '1px solid #FF9900' : '1px solid #E0E0E0',
          borderRadius: '4px',
          bgcolor: theme.palette.info.light,
          boxShadow: 'none',
          '&:hover': { border: Boolean(error) ? '1px solid #FF9900' : '1px solid #E0E0E0' },
          '&.Mui-focused': { border: Boolean(error) ? '1px solid #FF9900' : '1px solid #E0E0E0' },
        },
        '& .MuiSelect-select.MuiSelect-select': {
          padding: '0px',
          paddingRight: '0px !important',
          display: 'flex',
          height: '56px',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiSvgIcon-root.MuiSelect-icon': { display: 'none' },
        '& svg': { fill: '#fff', stroke: '#fff', width: '32px', height: '32px' },
      }
    : null;

  const modifiedOption =
    Array.isArray(value) && [...value]?.includes('managers')
      ? options.filter((i) => i.role !== UserRole.Manager)
      : options;

  const baseTextField = (
    <BaseTextField
      onChange={(e) => {
        onChange(e);
        handleChange?.(e as ChangeEvent<HTMLInputElement>);
      }}
      value={value}
      error={Boolean(error)}
      select
      sx={
        isIconSelect
          ? iconSelectSx
          : [
              disableDropdown
                ? disableStyles
                : multipleSelect
                ? multipleSelectStyles
                : defaultStyles,
              sx
                ? {
                    minHeight: '56px',
                    height: 'auto',
                    maxHeight: '92px',
                    '& .MuiInputBase-root': sx as object,
                  }
                : null,
            ]
      }
      InputProps={{}}
      SelectProps={{
        multiple: multipleSelect,
        disableUnderline: true,
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          sx: {
            '.MuiList-root': {
              maxHeight: '320px',
            },
            ...dropdownContainerSx,
          },
        },
        displayEmpty: true,
        renderValue: (values) => {
          if (!(values && (values as string[]).length)) {
            return (
              <>
                {categoryLength ? (
                  <CustomSelectedValue
                    key="selectedcategory"
                    icon=""
                    label={
                      categoryLength === 1
                        ? t('selectController.allUsersInSelectedCategory')
                        : t('selectController.allUsersInSelectedCategories')
                    }
                    identify="selectedcategory"
                    type="selectedcategory"
                  />
                ) : (
                  <Placeholder>{placeholder}</Placeholder>
                )}
              </>
            );
          }

          const handleDeleteChip = (deletedValue: string) => {
            const updated = (values as string[]).filter((v) => v !== deletedValue);
            onChange(updated);
            handleChange?.({
              target: { value: updated },
            } as unknown as ChangeEvent<HTMLInputElement>);
          };

          return multipleSelect ? (
            <MultipleSelectWrap>
              {(values as string[]).map((value) => {
                const option = options.find((el) => el.value === value);
                return (
                  <>
                    {!disableDropdown && option?.label && (
                      <CustomSelectedValue
                        key={value}
                        icon={option?.icon}
                        label={`${option?.label}` as string}
                        identify={name}
                        type={type}
                        onDelete={handleDeleteChip}
                        value={value}
                      />
                    )}
                  </>
                );
              })}
              {categoryLength ? (
                <CustomSelectedValue
                  key="mulitselectedcategory"
                  icon=""
                  label={
                    categoryLength === 1
                      ? 'All Users in Selected Category'
                      : 'All Users in Selected Categories'
                  }
                  identify="mulitselectedcategory"
                  type="mulitselectedcategory"
                />
              ) : (
                <></>
              )}
            </MultipleSelectWrap>
          ) : (
            <>
              {categoryLength ? (
                <CustomSelectedValue
                  key="test"
                  icon=""
                  label={
                    categoryLength === 1
                      ? 'All Users in Selected Category'
                      : 'All Users in Selected Categories'
                  }
                  identify="hjhj"
                  type="test"
                />
              ) : (
                <></>
              )}
              <CustomSelectedValue
                key={value}
                icon={options.find((el) => el.value === value)?.icon}
                label={options.find((el) => el.value === value)?.label as string}
                identify={name}
                type={type}
              />
            </>
          );
        },
        IconComponent: SelectExpandIcon,
        value: value || [],
        onClick: (event) => {
          if (disableDropdown) {
            event.preventDefault();
          }
        },
      }}
      FormHelperTextProps={isIconSelect ? { sx: { display: 'none' } } : callerFormHelperTextProps}
      {...otherProps}
    >
      {withDefaultEmptyOption && (
        <MenuItem value="">
          <em>{emptyOptionText}</em>
        </MenuItem>
      )}

      {modifiedOption.map(({ value, label, disabled, role, isManager }) => (
        <MenuItem
          key={value}
          value={value}
          sx={{
            ...menuItemStyles,
            display: disabled && value === '' ? 'none' : 'block',
            ...optionSx,
          }}
          disabled={disabled || disableDropdown}
        >
          {label}
          {(isManager || role === UserRole.Manager) && '*'}
        </MenuItem>
      ))}
    </BaseTextField>
  );

  const errorBox =
    error && error.message ? (
      <FieldErrorBox sx={isIconSelect ? { minWidth: '250px' } : undefined}>
        <InfoIcon />
        <FieldErrorText>{error.message}</FieldErrorText>
      </FieldErrorBox>
    ) : null;

  if (isIconSelect) {
    return (
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {baseTextField}
        {errorBox}
      </Box>
    );
  }

  return (
    <>
      {baseTextField}
      {errorBox}
    </>
  );
}
